import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, Divider, IconButton, Menu, MenuItem, Tooltip, Typography, useColorScheme, useTheme } from "@mui/material";
import { LoaderResponse } from "./types";
import {
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  Label as LabelIcon,
} from '@mui/icons-material';
import api, { ApiError } from "../../lib/api";
import { LoaderFunctionArgs, useLoaderData, useParams } from "react-router-dom";
import OrgGraphVis, { OrgGraphVisRef } from "org-graph/OrgGraphVis";
import { NodeLabelSettings, EdgeLabelSettings } from "org-graph";
import '../../components/AccordionStyles.css';
import { useAuth } from "react-oidc-context";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";


function preloadFonts() {
  const fonts = Array.from(document.fonts).filter(font =>
    (font.family === "Font Awesome 6 Brands" && font.weight === "400") ||
    (font.family === "Font Awesome 6 Free" && font.weight === "900"));

  return Promise.all(fonts.map(font => font.load()));
}

export async function ShortestPathcompanyAssessmentLoader({ params }: LoaderFunctionArgs) {
  const fontPromise = preloadFonts();

  try {
    const data = await api.post('/v1/companyrelationship', {
      orgNumberSource: params.orgNumber1,
      orgNumberTarget: params.orgNumber2,
    });

    await fontPromise;
    return { status: 200, data };
  } catch (err) {
    await fontPromise;
    if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
      return {
        status: err.status,
        errorMessage: typeof err.body === 'string' ? err.body : JSON.stringify(err.body),
      };
    }
    throw err;
  }
}


function ShortestPathOrgGraphCard({ shortestPathData }: { shortestPathData: any }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const { mode } = useColorScheme();
  const muiTheme = useTheme();
  const isDarkMode = mode === 'dark';
  const sourceNodeId = shortestPathData.graphData.metadata.sourceNodeId;
  const graphMetaData = shortestPathData.graphData.metadata;
  const { orgNumber1, orgNumber2 } = useParams();

  const degreesOfSeparation = shortestPathData.degreesOfSeparation;
  const showFilterButtonsValue = false;

  const orgGraphRef = useRef<OrgGraphVisRef>(null);

  const [labelAnchorEl, setLabelAnchorEl] = useState<HTMLElement | null>(null);
  const [nodeLabelSettings, setNodeLabelSettings] = useState<NodeLabelSettings | null>(null);
  const [edgeLabelSettings, setEdgeLabelSettings] = useState<EdgeLabelSettings | null>(null);

  const handleLabelClick = (event: React.MouseEvent<HTMLElement>) => {
    // Read current settings from the graph component when opening
    if (orgGraphRef.current) {
      setNodeLabelSettings(orgGraphRef.current.getNodeLabelSettings());
      setEdgeLabelSettings(orgGraphRef.current.getEdgeLabelSettings());
    }
    setLabelAnchorEl(event.currentTarget);
  };

  const handleLabelClose = () => {
    setLabelAnchorEl(null);
  };

  const handleNodeLabelToggle = (key: keyof NodeLabelSettings) => {
    if (!nodeLabelSettings || !orgGraphRef.current) return;
    const updated = { ...nodeLabelSettings, [key]: !nodeLabelSettings[key] };
    setNodeLabelSettings(updated);
    orgGraphRef.current.setNodeLabelSettings(updated);
  };

  const handleEdgeLabelToggle = (key: keyof EdgeLabelSettings) => {
    if (!edgeLabelSettings || !orgGraphRef.current) return;
    const updated = { ...edgeLabelSettings, [key]: !edgeLabelSettings[key] };
    setEdgeLabelSettings(updated);
    orgGraphRef.current.setEdgeLabelSettings(updated);
  };

  const handleToggleAllLabels = () => {
    if (!nodeLabelSettings || !edgeLabelSettings || !orgGraphRef.current) return;
    const nodeVals = Object.values(nodeLabelSettings);
    const edgeVals = Object.values(edgeLabelSettings);
    const turnOn = nodeVals.some(v => !v) || edgeVals.some(v => !v);
    const newNodes = Object.keys(nodeLabelSettings).reduce((acc, k) => {
      acc[k as keyof NodeLabelSettings] = turnOn;
      return acc;
    }, {} as NodeLabelSettings);
    const newEdges = Object.keys(edgeLabelSettings).reduce((acc, k) => {
      acc[k as keyof EdgeLabelSettings] = turnOn;
      return acc;
    }, {} as EdgeLabelSettings);
    setNodeLabelSettings(newNodes);
    setEdgeLabelSettings(newEdges);
    orgGraphRef.current.setNodeLabelSettings(newNodes);
    orgGraphRef.current.setEdgeLabelSettings(newEdges);
  };

  const handleSaveGraph = () => {
    if (orgGraphRef.current) {
      const nodes = shortestPathData.graphData.nodes;
      // Try to find nodes by ID (orgNumber) to get their names/labels
      const nodeA = nodes.find((n: any) => n.id === orgNumber1);
      const nodeB = nodes.find((n: any) => n.id === orgNumber2);
      
      // Use label or name property, fallback to orgNumber
      const nameA = nodeA?.label || nodeA?.name || orgNumber1 || 'CompanyA';
      const nameB = nodeB?.label || nodeB?.name || orgNumber2 || 'CompanyB';
      
      // Sanitize names for filename
      const sanitize = (name: string) => name.replace(/[^a-zA-Z0-9\u00C0-\u00FF]/g, '_');
      
      const now = new Date();
      const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      
      const filename = `Relationship_${sanitize(nameA)}_${sanitize(nameB)}_${dateStr}.png`;
      
      orgGraphRef.current.exportGraphAsPNG(filename);
    }
  };

  useEffect(() => {
    // Force background color override since OrgGraphVis defaults to black when no orgNumber is present
    const container = document.querySelector('#org-graph-card .org-graph-container') as HTMLElement;
    if (container) {
      container.style.setProperty('background-color', isDarkMode ? '#000000' : muiTheme.palette.background.default, 'important');
    }
  }, [isDarkMode, muiTheme.palette.background.default]);

  return (
    <Accordion defaultExpanded={true} className="custom-accordion" sx={{ width: '100%' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} className="custom-accordion-summary">
        <Typography variant="h6" className="custom-accordion-summary">{t('shortestPath.companyGraph')}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, m: 0, height: '100vh', minHeight: 800, display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" flexDirection="column" width="100%" flexGrow={1} minHeight={0} height="100%">
          <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1}>
            <Typography variant="h6" align="left">
              {t('shortestPath.degrees', { count: degreesOfSeparation })}
            </Typography>
            <Tooltip title={t('shortestPath.exportPng')}>
              <IconButton onClick={handleSaveGraph} size="small">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Label Settings Menu (anchored to the label button rendered in topBarContent) */}
          <Menu
            anchorEl={labelAnchorEl}
            open={Boolean(labelAnchorEl)}
            onClose={handleLabelClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
              <Button
                size="small"
                variant="text"
                onClick={handleToggleAllLabels}
                sx={{ textTransform: 'none' }}
              >
                {t('graph.labels.toggleAll')}
              </Button>
            </Box>

            <Box sx={{ px: 2, py: 1, display: 'flex', gap: 3, minWidth: 450 }}>
              {/* Node Labels Column */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: muiTheme.palette.primary.main }}>
                  {t('graph.labels.nodes')}
                </Typography>
                <MenuItem onClick={() => handleNodeLabelToggle('showOrganizationNumber')} sx={{ py: 0.5 }}>
                  <Checkbox checked={nodeLabelSettings?.showOrganizationNumber ?? true} size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">{t('common.fields.organizationNumber')}</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleNodeLabelToggle('showOrganizationName')} sx={{ py: 0.5 }}>
                  <Checkbox checked={nodeLabelSettings?.showOrganizationName ?? true} size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">{t('shortestPath.organizationName')}</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleNodeLabelToggle('showKonkursLikvidation')} sx={{ py: 0.5 }}>
                  <Checkbox checked={nodeLabelSettings?.showKonkursLikvidation ?? true} size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">{t('graph.labels.bankruptcyLiquidation')}</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleNodeLabelToggle('showSkuldsaldo')} sx={{ py: 0.5 }}>
                  <Checkbox checked={nodeLabelSettings?.showSkuldsaldo ?? true} size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">{t('graph.labels.kfmDebt')}</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleNodeLabelToggle('showAddress')} sx={{ py: 0.5 }}>
                  <Checkbox checked={nodeLabelSettings?.showAddress ?? true} size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">{t('graph.address')}</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleNodeLabelToggle('showName')} sx={{ py: 0.5 }}>
                  <Checkbox checked={nodeLabelSettings?.showName ?? true} size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">{t('graph.labels.customLinkingTitle')}</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleNodeLabelToggle('showFunktionarNamn')} sx={{ py: 0.5 }}>
                  <Checkbox checked={nodeLabelSettings?.showFunktionarNamn ?? true} size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">{t('graph.labels.personName')}</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleNodeLabelToggle('showFunktionarPersonalNumber')} sx={{ py: 0.5 }}>
                  <Checkbox checked={nodeLabelSettings?.showFunktionarPersonalNumber ?? true} size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">{t('common.fields.personalNumber')}</Typography>
                </MenuItem>
              </Box>

              {/* Divider */}
              <Divider orientation="vertical" flexItem />

              {/* Edge Labels Column */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: muiTheme.palette.primary.main }}>
                  {t('graph.labels.relationships')}
                </Typography>
                <MenuItem onClick={() => handleEdgeLabelToggle('showRole')} sx={{ py: 0.5 }}>
                  <Checkbox checked={edgeLabelSettings?.showRole ?? true} size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">{t('assessment.details.role')}</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleEdgeLabelToggle('showDateRange')} sx={{ py: 0.5 }}>
                  <Checkbox checked={edgeLabelSettings?.showDateRange ?? true} size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">{t('graph.labels.dateRange')}</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleEdgeLabelToggle('showDuration')} sx={{ py: 0.5 }}>
                  <Checkbox checked={edgeLabelSettings?.showDuration ?? true} size="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">{t('assessment.details.duration')}</Typography>
                </MenuItem>
              </Box>
            </Box>
          </Menu>
          <Box id="org-graph-card" sx={{ width: '100%', flexGrow: 1, minHeight: 800, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <OrgGraphVis
              ref={orgGraphRef}
              key={sourceNodeId} // Force a remount when the org number changes
              nodes={shortestPathData.graphData.nodes}
              relationships={shortestPathData.graphData.relationships}
              orgNumber={""} // Pass empty string
              graphMetaData={graphMetaData}
              showFilterButtons={showFilterButtonsValue}
              getAccessToken={() => auth.user?.access_token}
              isDarkMode={isDarkMode}
              topBarContent={
                <Tooltip title={t('graph.labelSettings')}>
                  <IconButton onClick={handleLabelClick} size="small">
                    <LabelIcon />
                  </IconButton>
                </Tooltip>
              }
            />
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

export default function ShortestPathCompanyProfileDisplay() {
  const { t } = useTranslation();
  const loaderData = useLoaderData() as LoaderResponse;

  if (loaderData.status !== 200 && loaderData.errorMessage) {
    return <Typography>{loaderData.errorMessage}</Typography>;
  }

  // If loader data connected = FALSE then show text saying "companies are not connected"
  if ('connected' in loaderData.data! && loaderData.data.connected === false) {
    return <Typography>{t('shortestPath.notConnected')}</Typography>;
  }

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <ShortestPathOrgGraphCard shortestPathData={loaderData.data!} />
    </Box>
  );
}
